import { i18n } from "../i18n/i18n";

import React, { Component, Fragment } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import routes from '../routes/routes'
import customerService from '../services/customer.service'
import contactSiteService from '../services/contactSite.service'
import licensePlateApi, {
  LicensePlateApplicableType,
  LicensePlateCreateRequest
} from '../apis/licensePlate.api'
import customerApi, { CustomerData } from '../apis/customer.api'
import { ContactSiteData } from '../apis/contactSite.api'
import ContentLayout from '../components/ContentLayout'
import ContentMain from '../components/ContentMain'
import Card from '../components/Card'
import CardButton from '../components/CardButton'
import Row from '../components/Row/Row'
import InputFile from '../components/InputFile'
import TemplateDownloader from '../components/TemplateDownloader'
import PageHeading from '../components/PageHeading'
import SearchPicker, { SearchPickerOption } from '../components/SearchPicker'
import Select from '../components/Select'
import RadioGroup, { RadioGroupItem } from '../components/RadioGroup'

interface LicensePlateUploadProps extends RouteComponentProps {}

interface LicensePlateUploadState {
  applicable: LicensePlateApplicableType
  customer: string
  customerId: number | null
  customerSite: string
  file: File | null
  filename: string
  customers: CustomerData[]
  customerSites: ContactSiteData[]
}

class LicensePlateUpload
  extends Component<LicensePlateUploadProps, LicensePlateUploadState> {
  applicables: RadioGroupItem[] = [
    { name: 'site', label: 'Site' },
    { name: 'customer', label: 'Customer' },
    { name: 'customerSite', label: 'Customer site' }
  ];

  constructor (props: LicensePlateUploadProps) {
    super(props)
    this.state = {
      applicable: 'site',
      customer: '',
      customerId: null,
      customerSite: '',
      file: null,
      filename: '',
      customers: [],
      customerSites: []
    }
    this.handleApplicableChange = this.handleApplicableChange.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleCustomerSearch = this.handleCustomerSearch.bind(this)
    this.handleCustomerSelect = this.handleCustomerSelect.bind(this)
    this.handleCustomerBlur = this.handleCustomerBlur.bind(this)
    this.handleInputFileChange = this.handleInputFileChange.bind(this)
    this.handleSaveClick = this.handleSaveClick.bind(this)
  }

  get customerSearchOptions () {
    return customerService.searchOptions(this.state.customers)
  }

  get customerSiteSelectOptions () {
    return contactSiteService.selectOptions(this.state.customerSites)
  }

  save () {
    const { applicable, customerId, customerSite, file } = this.state
    const payload: LicensePlateCreateRequest = {}
    if (applicable === 'customer') {
      if (customerId) payload.customer = customerId
    } else if (applicable === 'customerSite') {
      if (customerSite) payload.customerSite = Number(customerSite)
    }
    if (file) payload.licensePlates = file
    licensePlateApi.create(payload, licensePlate => {
      if (!licensePlate) return
      this.props.history.push(routes.licensePlates)
    })
  }

  handleInputFileChange (name: string, file: File | null, filename: string) {
    this.setState({ file, filename })
  }

  handleInputChange (name: string, value: string) {
    if (name === 'customer') {
      this.setState({ customerId: null, customer: value })
    } else if (name === 'customerSite') this.setState({ customerSite: value })
  }

  handleCustomerSearch (name: string, term: string) {
    customerApi.search(term, customers => {
      if (!customers) return
      this.setState({ customers })
    })
  }

  handleCustomerBlur (name: string) {
    this.setState({ customers: [] })
  }

  listCustomerSites () {
    const customerId = Number(this.state.customerId)
    customerApi.listSites(customerId, customerSites => {
      if (!customerSites) return
      this.setState({ customerSites })
    })
  }

  handleCustomerSelect (name: string, option: SearchPickerOption) {
    const customerId = option.id as number
    this.setState({ customers: [], customerId }, () => {
      this.listCustomerSites()
    })
  }

  handleApplicableChange (name: LicensePlateApplicableType) {
    this.setState({ applicable: name })
  }

  handleSaveClick () {
    this.save()
  }

  get customerField () {
    return (
      <SearchPicker
        name="customer"
        value={this.state.customer}
        options={this.customerSearchOptions}
        label={i18n("Customer")}
        onSearch={this.handleCustomerSearch}
        onChange={this.handleInputChange}
        onSelect={this.handleCustomerSelect}
        onBlur={this.handleCustomerBlur} />
    )
  }

  get fields () {
    const { applicable } = this.state
    const customerSiteFields = () => {
      return (
        <Fragment>
          {this.customerField}
          <Select
            name="customerSite"
            value={this.state.customerSite}
            options={this.customerSiteSelectOptions}
            label={i18n("Customer site")}
            onChange={this.handleInputChange} />
        </Fragment>
      )
    }
    return (applicable === 'customerSite')
      ? customerSiteFields()
      : (applicable === 'customer')
          ? this.customerField
          : null
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="twoThird">
          <Card>
            <PageHeading
              text={i18n("Upload License Plate")}
              doc='license-plates-upload-site-license-plates' />
            <Row column={1}>
              <RadioGroup
                label={i18n("Applicable to")}
                radios={this.applicables}
                checked={this.state.applicable}
                onChange={this.handleApplicableChange} />
            </Row>
            <Row column={2}>
              {this.fields}
            </Row>
            <Row column={2}>
              <div className="block">
                <InputFile
                  name="licensePlateNumbers"
                  filename={this.state.filename}
                  accept=".csv"
                  label="License plate numbers (LPNs)"
                  onChange={this.handleInputFileChange} />
                <TemplateDownloader
                  text="Dowload sample LPN list template"
                  filename="licensePlate" />
              </div>
            </Row>
            <CardButton
              proceedText={i18n("Save")}
              cancelText={i18n("Cancel")}
              onProceedClick={this.handleSaveClick} />
          </Card>
        </ContentMain>
      </ContentLayout>
    )
  }
}

export default withRouter(LicensePlateUpload)
